import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Users, ClipboardCheck, CalendarClock, Package, ShoppingCart, TrendingUp, Printer, Trash2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StaffActivity = () => {
  const { getAuthHeader } = useAuth();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/reports/staff-activity?days=7`, { headers: getAuthHeader() });
      setActivity(res.data);
    } catch {
      toast.error('Failed to load staff activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchActivity(); }, [getAuthHeader]);

  const handlePrint = () => window.print();

  const handleClear = async () => {
    if (!window.confirm('Clear all activity data for the last 7 days? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/reports/staff-activity`, { headers: getAuthHeader() });
      toast.success('Activity report cleared');
      fetchActivity();
    } catch {
      toast.error('Failed to clear activity');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const staffEntries = Object.entries(activity?.staff_summary || {});

  return (
    <div className="space-y-6 print:space-y-4" data-testid="staff-activity-page">
      {/* Header */}
      <div className="flex items-center justify-between print:mb-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Staff Activity</h1>
          <p className="text-slate-500 mt-1">Last 7 days overview</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-activity-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Report
          </Button>
          <Button variant="destructive" size="sm" onClick={handleClear} data-testid="clear-activity-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Clear Report
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Stock Takes', value: activity?.totals?.stock_takes || 0, icon: ClipboardCheck, bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-700', sub: 'text-blue-600' },
          { label: 'Stock Adjustments', value: activity?.totals?.stock_adjustments || 0, icon: Package, bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-700', sub: 'text-emerald-600' },
          { label: 'Expiry Updates', value: activity?.totals?.expiry_updates || 0, icon: CalendarClock, bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700', sub: 'text-amber-600' },
          { label: 'Customer Orders', value: activity?.totals?.customer_orders || 0, icon: ShoppingCart, bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-700', sub: 'text-purple-600' },
          { label: 'Sales Processed', value: activity?.totals?.sales || 0, icon: TrendingUp, bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-700', sub: 'text-orange-600' },
        ].map((card) => (
          <Card key={card.label} className={`${card.bg} border ${card.border} rounded-sm`}>
            <CardContent className="p-5 text-center">
              <card.icon className={`w-6 h-6 mx-auto mb-2 ${card.sub}`} />
              <p className={`text-3xl font-bold ${card.text}`}>{card.value}</p>
              <p className={`text-xs font-medium mt-1 ${card.sub}`}>{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Staff Breakdown Table */}
      <Card className="border rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-xl font-bold uppercase tracking-tight flex items-center gap-2">
            <Users className="w-5 h-5" /> Per-Staff Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {staffEntries.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="staff-breakdown-table">
                <thead className="bg-slate-800 text-white">
                  <tr>
                    <th className="text-left p-4 font-semibold">Staff Member</th>
                    <th className="text-center p-4 font-semibold">Stock Takes</th>
                    <th className="text-center p-4 font-semibold">Adjustments</th>
                    <th className="text-center p-4 font-semibold">Expiry Updates</th>
                    <th className="text-center p-4 font-semibold">Customer Orders</th>
                    <th className="text-center p-4 font-semibold">Sales</th>
                    <th className="text-center p-4 font-semibold">Total Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {staffEntries.map(([name, data], idx) => {
                    const total = data.stock_takes + data.stock_adjustments + data.expiry_updates + data.customer_orders + data.sales;
                    return (
                      <tr key={name} className={`border-t ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-orange-50 transition-colors`} data-testid={`staff-row-${name}`}>
                        <td className="p-4 font-semibold text-slate-900 text-base">{name}</td>
                        <td className="text-center p-4">
                          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-base font-bold ${data.stock_takes > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
                            {data.stock_takes}
                          </span>
                        </td>
                        <td className="text-center p-4">
                          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-base font-bold ${data.stock_adjustments > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                            {data.stock_adjustments}
                          </span>
                        </td>
                        <td className="text-center p-4">
                          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-base font-bold ${data.expiry_updates > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'}`}>
                            {data.expiry_updates}
                          </span>
                        </td>
                        <td className="text-center p-4">
                          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-base font-bold ${data.customer_orders > 0 ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-400'}`}>
                            {data.customer_orders}
                          </span>
                        </td>
                        <td className="text-center p-4">
                          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-base font-bold ${data.sales > 0 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-400'}`}>
                            {data.sales}
                          </span>
                        </td>
                        <td className="text-center p-4">
                          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-base font-bold bg-slate-900 text-white">
                            {total}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <CalendarClock className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <p className="text-lg font-medium">No staff activity in the last 7 days</p>
              <p className="text-sm mt-1">Activity will appear here once staff perform tasks</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manager-PIN Margin Overrides */}
      {activity?.markup_overrides?.length > 0 && (
        <Card className="border-2 border-amber-200 rounded-sm" data-testid="markup-overrides-card">
          <CardHeader className="pb-3 bg-amber-50">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-tight flex items-center gap-2 text-amber-900">
              <ShieldAlert className="w-5 h-5" /> Manager-PIN Margin Overrides ({activity.markup_overrides.length})
            </CardTitle>
            <p className="text-xs text-amber-800 mt-1">
              Every time staff used the manager code to save a product under 42% margin during stock receiving.
            </p>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="markup-overrides-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-semibold text-slate-600">When</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Staff</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Product</th>
                    <th className="text-right p-3 font-semibold text-slate-600">Cost</th>
                    <th className="text-right p-3 font-semibold text-slate-600">Old Retail</th>
                    <th className="text-right p-3 font-semibold text-slate-600">New Retail</th>
                    <th className="text-center p-3 font-semibold text-slate-600">Margin</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Reason</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Store</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.markup_overrides.map((mo) => (
                    <tr key={mo.id} className="border-t hover:bg-amber-50">
                      <td className="p-3 text-slate-500 text-xs whitespace-nowrap">
                        {formatDateTime(mo.created_at)}
                      </td>
                      <td className="p-3 font-medium">{mo.performed_by || 'Unknown'}</td>
                      <td className="p-3">{mo.product_name}</td>
                      <td className="p-3 text-right">${(mo.unit_cost || 0).toFixed(2)}</td>
                      <td className="p-3 text-right text-slate-500">{mo.old_sell_price != null ? `$${mo.old_sell_price.toFixed(2)}` : '—'}</td>
                      <td className="p-3 text-right font-semibold">${(mo.new_sell_price || 0).toFixed(2)}</td>
                      <td className="p-3 text-center">
                        <Badge className="bg-amber-100 text-amber-800">{mo.margin_percent != null ? `${mo.margin_percent}%` : (mo.markup_percent != null ? `${mo.markup_percent}% mkup` : '—')}</Badge>
                      </td>
                      <td className="p-3 text-slate-700 italic max-w-xs">{mo.reason}</td>
                      <td className="p-3 uppercase text-xs text-slate-500">{mo.store}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Stock Takes */}
      {activity?.recent_stock_takes?.length > 0 && (
        <Card className="border rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-tight flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" /> Recent Stock Takes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-semibold text-slate-600">Staff</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Store</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Category/Filter</th>
                    <th className="text-center p-3 font-semibold text-slate-600">Status</th>
                    <th className="text-right p-3 font-semibold text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.recent_stock_takes.map((st) => (
                    <tr key={st.id} className="border-t hover:bg-slate-50">
                      <td className="p-3 font-medium">{st.created_by || 'Unknown'}</td>
                      <td className="p-3 text-slate-600">{st.store || '—'}</td>
                      <td className="p-3 text-slate-600">{st.filter_name || st.filter_value || 'All Products'}</td>
                      <td className="text-center p-3">
                        <Badge variant={st.status === 'completed' ? 'default' : 'secondary'}>
                          {st.status}
                        </Badge>
                      </td>
                      <td className="text-right p-3 text-slate-500 text-sm">
                        {formatDateTime(st.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Customer Orders */}
      {activity?.recent_customer_orders?.length > 0 && (
        <Card className="border rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-tight flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" /> Recent Customer Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-semibold text-slate-600">Customer</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Items</th>
                    <th className="text-left p-3 font-semibold text-slate-600">Store</th>
                    <th className="text-center p-3 font-semibold text-slate-600">Status</th>
                    <th className="text-right p-3 font-semibold text-slate-600">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.recent_customer_orders.map((co) => (
                    <tr key={co.id} className="border-t hover:bg-slate-50">
                      <td className="p-3 font-medium">{co.customer_name}</td>
                      <td className="p-3 text-slate-600 max-w-xs truncate">{co.items}</td>
                      <td className="p-3 text-slate-600">{co.store || '—'}</td>
                      <td className="text-center p-3">
                        <Badge variant={co.status === 'arrived' ? 'default' : co.status === 'ordered' ? 'secondary' : 'destructive'}>
                          {co.status}
                        </Badge>
                      </td>
                      <td className="text-right p-3 text-slate-500 text-sm">
                        {formatDateTime(co.updated_at || co.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          nav, .print\\:hidden, [data-testid="print-activity-btn"], [data-testid="clear-activity-btn"] { display: none !important; }
          body { background: white !important; }
          .space-y-6 > * { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default StaffActivity;
